import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFocusMode } from '../context/FocusModeContext';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Briefcase, TrendingUp, DollarSign, GraduationCap, Search, Star, X,
  ChevronRight, ChevronDown, ExternalLink, Focus, BarChart3, Users, BookOpen,
  Wrench, Building2, Info, Check, Target, Award, Zap, ShieldCheck, Loader2,
  Sparkles, ArrowUp, ArrowRight, Clock, MapPin, Monitor, Heart, Brain,
  Bookmark, BookmarkCheck, GitCompare, ThumbsDown, ThumbsUp, Plus, Rocket,
  Shield, Plane, Trophy, Palette, Microscope, Landmark, Scale, Leaf, Cpu,
  Megaphone, Compass, Building, Sparkle,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import PatriciaPageGuide from '../components/PatriciaPageGuide';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Tag styling ─── */
const TAG_STYLE = {
  high_demand: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  ai_related:  'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  ai_resistant: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
};

export function CareerTags({ tags, className = '' }) {
  if (!tags || !tags.length) return null;
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`} data-testid="career-tags">
      {tags.map((t) => (
        <span key={t.key} title={t.why}
          className={`text-[10px] px-2 py-0.5 rounded-full border ${TAG_STYLE[t.key] || 'bg-[rgba(6,23,54,0.5)]/40 text-slate-300 border-slate-600'}`}
          data-testid={`career-tag-${t.key}`}>
          {t.label}
        </span>
      ))}
    </div>
  );
}

/* ─── Salary percentile bar ─── */
function SalaryBar({ label, value, maxValue, color = 'blue' }) {
  if (!value) return null;
  const pct = Math.min((value / maxValue) * 100, 100);
  const formatted = `$${Math.round(value).toLocaleString()}`;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-[#C6D4E8] w-12 text-xs text-right shrink-0">{label}</span>
      <div className="flex-1 bg-slate-800/60 rounded-full h-5 relative overflow-hidden">
        <motion.div
          initial={{ width: 0 }} animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className={`h-full rounded-full bg-gradient-to-r ${
            color === 'emerald' ? 'from-emerald-600 to-emerald-400' :
            color === 'cyan'    ? 'from-cyan-600 to-cyan-400'    :
            'from-blue-600 to-blue-400'}`}
        />
        <span className="absolute right-2 top-0 h-full flex items-center text-xs font-mono text-white/90">{formatted}</span>
      </div>
    </div>
  );
}

/* ─── BLS panel ─── */
function BlsDataPanel({ bls }) {
  if (!bls || !bls.median) return null;
  const maxVal = bls.pct_90 || bls.median * 1.5;
  return (
    <div className="p-5 bg-slate-800/30 rounded-xl border border-[rgba(57,227,220,0.12)]/50" data-testid="bls-data-panel">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-[#39E3DC]" />
        <h4 className="text-base font-semibold text-white">Salary Breakdown</h4>
        <span className="ml-auto text-xs text-slate-300 bg-slate-800 px-2 py-0.5 rounded-full">BLS May {bls.data_year || '2024'}</span>
      </div>
      <div className="space-y-2 mb-4">
        <SalaryBar label="90th" value={bls.pct_90} maxValue={maxVal} color="emerald" />
        <SalaryBar label="75th" value={bls.pct_75} maxValue={maxVal} color="cyan" />
        <SalaryBar label="Median" value={bls.median} maxValue={maxVal} color="blue" />
        <SalaryBar label="25th" value={bls.pct_25} maxValue={maxVal} color="cyan" />
        <SalaryBar label="10th" value={bls.pct_10} maxValue={maxVal} color="emerald" />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="p-3 bg-slate-900/50 rounded-lg text-center">
          <p className="text-xs text-slate-300">Annual Mean</p>
          <p className="text-emerald-400 font-mono font-bold">${Math.round(bls.annual_mean || 0).toLocaleString()}</p>
        </div>
        {bls.employment && (
          <div className="p-3 bg-slate-900/50 rounded-lg text-center">
            <p className="text-xs text-slate-300">Total Employment</p>
            <p className="text-cyan-400 font-mono font-bold">{bls.employment_formatted || Math.round(bls.employment).toLocaleString()}</p>
          </div>
        )}
      </div>
      <p className="text-[10px] text-slate-300 mt-3 text-right">Source: U.S. Bureau of Labor Statistics, OEWS</p>
    </div>
  );
}

/* ─── Section wrapper for the deep-dive ─── */
function DeepSection({ icon: Icon, title, children, testId }) {
  return (
    <div className="rounded-2xl border border-[rgba(57,227,220,0.12)]/40 bg-slate-800/20 p-5" data-testid={testId}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-cyan-300" />
        </div>
        <h3 className="text-white font-semibold text-sm">{title}</h3>
      </div>
      {children}
    </div>
  );
}

/* ─── Pill tag ─── */
function Pill({ children, hue = 'cyan' }) {
  const colors = {
    cyan:    'bg-cyan-500/12 text-cyan-100 ring-cyan-400/30',
    fuchsia: 'bg-fuchsia-500/12 text-fuchsia-100 ring-fuchsia-400/30',
    amber:   'bg-amber-500/12 text-amber-100 ring-amber-400/30',
    emerald: 'bg-emerald-500/12 text-emerald-100 ring-emerald-400/30',
    slate:   'bg-slate-800 text-slate-200 ring-slate-700',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ring-1 ${colors[hue] || colors.cyan}`}>
      {children}
    </span>
  );
}

/* ─── Career Detail View (full-page overlay) ─── */
function CareerDetailView({ career, onClose }) {
  const { getAuthHeader } = useAuth();
  const [deepProfile, setDeepProfile] = useState(null);
  const [deepLoading, setDeepLoading] = useState(false);
  const [settingCareer, setSettingCareer] = useState(false);
  const [careerSet, setCareerSet] = useState(false);
  const [addingGoal, setAddingGoal] = useState(false);
  const [addedGoal, setAddedGoal] = useState(false);
  const [confirmChange, setConfirmChange] = useState(null);

  useEffect(() => {
    if (!career.soc_code) return;
    setDeepLoading(true);
    axios.get(`${API}/careers/${career.soc_code}/profile`, getAuthHeader())
      .then(r => setDeepProfile(r.data))
      .catch(() => {})
      .finally(() => setDeepLoading(false));
  }, [career.soc_code, getAuthHeader]);

  const setAsCareerGoal = async (confirmed = false) => {
    setSettingCareer(true);
    try {
      const res = await axios.post(`${API}/career/target`, { title: career.title, confirm: confirmed }, { ...getAuthHeader(), timeout: 20000 });
      if (res.data.requires_confirmation) { setConfirmChange(res.data); return; }
      setCareerSet(true); setConfirmChange(null);
      toast.success(`Career goal set to "${career.title}"`);
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Could not set career goal.'); }
    finally { setSettingCareer(false); }
  };

  const addToGoals = async () => {
    setAddingGoal(true);
    try {
      await axios.post(`${API}/goals`, { title: `Become a ${career.title}`, description: career.description?.slice(0, 300) || '', category: 'career', priority: 'high' }, { ...getAuthHeader(), timeout: 20000 });
      setAddedGoal(true); toast.success(`"${career.title}" added to your goals!`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add to goals.'); }
    finally { setAddingGoal(false); }
  };

  const hasBls = career.bls_data && career.bls_data.median;
  const dp = deepProfile;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#0a1729]/85 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto"
      onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
        transition={{ type: 'spring', damping: 28, stiffness: 300 }}
        className="bg-slate-900 border border-[rgba(57,227,220,0.12)]/60 rounded-2xl w-full max-w-4xl my-6 mx-4 shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="relative p-6 pb-5 bg-gradient-to-br from-cyan-600/15 via-blue-600/10 to-transparent border-b border-slate-800">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 text-[#C6D4E8] hover:text-white transition-colors" data-testid="career-detail-close">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-cyan-500/15 flex items-center justify-center shrink-0">
              <Briefcase className="w-7 h-7 text-cyan-300" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-white leading-tight">{career.title}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                {career.match_score && (
                  <span className="flex items-center gap-1 text-amber-400 text-sm font-semibold">
                    <Star className="w-3.5 h-3.5 fill-amber-400" /> {career.match_score}% match
                  </span>
                )}
                {career.soc_code && <span className="text-slate-300 text-xs bg-slate-800 px-2 py-0.5 rounded">SOC {career.soc_code}</span>}
                {career.job_zone && <span className="text-slate-300 text-xs bg-slate-800 px-2 py-0.5 rounded">Job Zone {career.job_zone}</span>}
                <CareerTags tags={career.tags} />
              </div>
            </div>
          </div>

          {/* Key stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            {[
              { icon: DollarSign, label: 'Median Salary', value: hasBls ? `$${Math.round(career.bls_data.median).toLocaleString()}` : (career.salary_range || '-'), accent: 'text-emerald-400' },
              { icon: TrendingUp, label: 'Growth', value: dp?.projected_growth_pct_10yr != null ? `+${dp.projected_growth_pct_10yr}%` : (career.growth_outlook || '-'), accent: 'text-cyan-400' },
              { icon: GraduationCap, label: 'Education', value: dp?.typical_education || career.education_required || '-', accent: 'text-blue-300' },
              { icon: Zap, label: 'AI Exposure', value: dp?.automation_exposure_pct != null ? `${dp.automation_exposure_pct}%` : '-', accent: 'text-amber-400' },
            ].map(({ icon: I, label, value, accent }) => (
              <div key={label} className="rounded-xl bg-slate-800/40 border border-[rgba(57,227,220,0.12)]/30 p-3">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#C6D4E8] font-semibold"><I className="w-3 h-3" /> {label}</div>
                <div className={`font-bold mt-1 text-sm ${accent}`}>{value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="p-6 space-y-6 max-h-[65vh] overflow-y-auto">

          {/* What does this person do? */}
          <div>
            <h2 className="text-lg font-bold text-white mb-2">What does a {career.title} do?</h2>
            <p className="text-slate-300 text-base leading-relaxed">{career.description}</p>
          </div>

          {/* Day in the life */}
          {(dp?.day_in_life || career.day_in_life) && (
            <DeepSection icon={Clock} title="A Day in the Life" testId="deep-day-in-life">
              <p className="text-slate-300 text-sm leading-relaxed">{dp?.day_in_life || career.day_in_life}</p>
            </DeepSection>
          )}

          {/* Loading indicator */}
          {deepLoading && (
            <div className="flex items-center gap-3 py-8 justify-center text-[#C6D4E8]">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading full career profile from O*NET...</span>
            </div>
          )}

          {/* Salary breakdown */}
          {hasBls && <BlsDataPanel bls={career.bls_data} />}

          {/* Fit score */}
          {dp?.student_fit?.match_score != null && (
            <DeepSection icon={Target} title="Your Fit Score" testId="deep-fit">
              <div className="flex items-center gap-4">
                <div className="shrink-0 text-center">
                  <div className="text-4xl font-black text-cyan-300">{dp.student_fit.match_score}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#C6D4E8] font-bold mt-1">out of 100</div>
                </div>
                {dp.student_fit.components && (
                  <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { label: 'Interests', val: dp.student_fit.components.interest_fit, max: 60 },
                      { label: 'Skills', val: dp.student_fit.components.skills_overlap, max: 20 },
                      { label: 'Values', val: dp.student_fit.components.values_alignment, max: 20 },
                      { label: 'AI-Proof', val: dp.student_fit.components.automation_resilience, max: 20 },
                    ].map(({ label, val, max }) => (
                      <div key={label} className="rounded-lg bg-slate-800/60 p-2.5">
                        <div className="text-xs font-bold text-cyan-200">{label}</div>
                        <div className="text-white font-black">{val ?? 0}<span className="text-slate-300 text-xs">/{max}</span></div>
                        <div className="w-full h-1.5 bg-slate-900 rounded-full overflow-hidden mt-1">
                          <div className="h-full bg-gradient-to-r from-cyan-500 to-sky-400 rounded-full" style={{ width: `${max ? ((val ?? 0) / max) * 100 : 0}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DeepSection>
          )}

          {/* Tasks */}
          {(dp?.top_tasks?.length > 0 || career.typical_tasks?.length > 0) && (
            <DeepSection icon={Briefcase} title="What You'll Do Every Day" testId="deep-tasks">
              <ul className="space-y-2">
                {(dp?.top_tasks || career.typical_tasks || []).map((t, i) => (
                  <li key={i} className="text-sm text-slate-200 flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-cyan-500/15 text-cyan-300 text-xs flex items-center justify-center shrink-0 mt-0.5 font-bold">{i + 1}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </DeepSection>
          )}

          {/* Skills + Abilities two-column */}
          {(dp?.top_skills?.length > 0 || career.skills_needed?.length > 0 || dp?.top_abilities?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(dp?.top_skills?.length > 0 || career.skills_needed?.length > 0) && (
                <DeepSection icon={Sparkles} title="Skills You'll Need" testId="deep-skills">
                  <div className="flex flex-wrap gap-1.5">
                    {(dp?.top_skills || career.skills_needed || []).map(s => <Pill key={s} hue="cyan">{s}</Pill>)}
                  </div>
                </DeepSection>
              )}
              {dp?.top_abilities?.length > 0 && (
                <DeepSection icon={Brain} title="Abilities" testId="deep-abilities">
                  <div className="flex flex-wrap gap-1.5">
                    {dp.top_abilities.map(s => <Pill key={s} hue="fuchsia">{s}</Pill>)}
                  </div>
                </DeepSection>
              )}
            </div>
          )}

          {/* Knowledge + Technology two-column */}
          {(dp?.knowledge_areas?.length > 0 || dp?.technology_skills?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dp?.knowledge_areas?.length > 0 && (
                <DeepSection icon={BookOpen} title="Knowledge Areas" testId="deep-knowledge">
                  <div className="flex flex-wrap gap-1.5">
                    {dp.knowledge_areas.map(s => <Pill key={s} hue="slate">{s}</Pill>)}
                  </div>
                </DeepSection>
              )}
              {dp?.technology_skills?.length > 0 && (
                <DeepSection icon={Monitor} title="Tools & Technology" testId="deep-tech">
                  <div className="flex flex-wrap gap-1.5">
                    {dp.technology_skills.map(s => <Pill key={s} hue="amber">{s}</Pill>)}
                  </div>
                </DeepSection>
              )}
            </div>
          )}

          {/* Work environment + context */}
          {(career.work_environment || dp?.work_context?.length > 0 || dp?.work_styles?.length > 0) && (
            <DeepSection icon={Building2} title="Work Environment" testId="deep-work-env">
              {career.work_environment && <p className="text-sm text-slate-300 mb-3">{career.work_environment}</p>}
              {dp?.work_context?.length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#C6D4E8] font-semibold mb-1.5">Context</div>
                  <div className="flex flex-wrap gap-1.5">
                    {dp.work_context.map(s => <Pill key={s} hue="emerald">{s}</Pill>)}
                  </div>
                </div>
              )}
              {dp?.work_styles?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#C6D4E8] font-semibold mb-1.5">Work Styles</div>
                  <div className="flex flex-wrap gap-1.5">
                    {dp.work_styles.map(s => <Pill key={s} hue="slate">{s}</Pill>)}
                  </div>
                </div>
              )}
            </DeepSection>
          )}

          {/* How to get started */}
          {(career.education_required || career.entry_path || dp?.credentials?.length > 0 || dp?.apprenticeships?.length > 0) && (
            <DeepSection icon={GraduationCap} title="How to Get Started" testId="deep-getting-started">
              {career.education_required && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#C6D4E8] font-semibold mb-1">Education Required</div>
                  <p className="text-sm text-slate-200">{career.education_required}</p>
                </div>
              )}
              {career.entry_path && (
                <div className="mb-3 p-3 bg-gradient-to-r from-blue-600/8 to-cyan-600/8 rounded-lg border border-blue-500/15">
                  <p className="text-sm text-slate-200">{career.entry_path}</p>
                </div>
              )}
              {dp?.credentials?.length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#C6D4E8] font-semibold mb-1.5">Helpful Credentials</div>
                  <div className="space-y-1.5">
                    {dp.credentials.map((c, i) => (
                      <div key={i} className="flex items-center justify-between rounded-lg bg-slate-800/40 px-3 py-2">
                        <div>
                          <span className="text-sm text-white font-medium">{c.name}</span>
                          {c.provider && <span className="text-xs text-[#C6D4E8] ml-2">by {c.provider}</span>}
                        </div>
                        {c.level && <Pill hue={c.level === 'entry' ? 'emerald' : 'fuchsia'}>{c.level}</Pill>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {dp?.apprenticeships?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#C6D4E8] font-semibold mb-1.5">Apprenticeships</div>
                  <div className="space-y-1.5">
                    {dp.apprenticeships.map((a, i) => (
                      <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 rounded-lg bg-slate-800/40 px-3 py-2 text-sm text-cyan-300 hover:text-cyan-200 transition-colors">
                        {a.program} <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </DeepSection>
          )}

          {/* Related careers / occupations */}
          {(dp?.related_occupations?.length > 0 || career.related_careers?.length > 0) && (
            <DeepSection icon={Users} title="Related Careers" testId="deep-related">
              <div className="grid sm:grid-cols-2 gap-2">
                {(dp?.related_occupations || []).map(r => (
                  <div key={r.soc_code} className="flex items-center justify-between rounded-lg bg-slate-800/40 px-3 py-2">
                    <span className="text-sm text-slate-200">{r.title}</span>
                    <span className="text-[10px] text-slate-300">{r.soc_code}</span>
                  </div>
                ))}
                {!dp?.related_occupations?.length && (career.related_careers || []).map((r, i) => (
                  <div key={i} className="rounded-lg bg-slate-800/40 px-3 py-2 text-sm text-slate-200">{r}</div>
                ))}
              </div>
            </DeepSection>
          )}

          {/* Sources */}
          {dp?.sources?.length > 0 && (
            <DeepSection icon={ShieldCheck} title="Data Sources" testId="deep-sources">
              <ul className="space-y-1.5">
                {dp.sources.map((s, i) => (
                  <li key={i} className="text-xs text-slate-300 flex items-start gap-2">
                    <Info className="w-3 h-3 mt-0.5 shrink-0 text-slate-300" />
                    <span><span className="text-slate-100 font-medium">{s.field}</span>, {s.source} <span className="text-slate-300">as of {s.as_of}</span></span>
                  </li>
                ))}
              </ul>
              {dp.data_completeness_pct != null && (
                <p className="mt-3 text-[11px] text-emerald-300 flex items-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" /> Data from O*NET and BLS, we don't make up salaries. {dp.data_completeness_pct}% complete.
                </p>
              )}
            </DeepSection>
          )}

          {/* O*NET attribution */}
          <div className="pt-4 border-t border-slate-800">
            <div className="flex items-start gap-2 p-3 bg-slate-800/20 rounded-lg">
              <Info className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
              <div className="text-[11px] text-slate-300 space-y-1">
                {hasBls && <p>Salary data: U.S. Bureau of Labor Statistics, OEWS, May {career.bls_data.data_year || '2024'}.</p>}
                <p>This site incorporates information from <a href="https://services.onetcenter.org/" target="_blank" rel="noopener noreferrer" className="text-[#39E3DC] underline">O*NET Web Services</a> by the U.S. Department of Labor, Employment and Training Administration (USDOL/ETA).</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer actions ── */}
        <div className="p-5 border-t border-slate-800 bg-slate-900 space-y-3">
          {confirmChange && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-sm text-amber-200 space-y-2">
              <p className="font-medium">Replace <span className="text-white">"{confirmChange.current?.title}"</span> with <span className="text-white">"{confirmChange.proposed?.title}"</span>?</p>
              <div className="flex gap-2 mt-1">
                <Button size="sm" onClick={() => setAsCareerGoal(true)} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs h-7">Yes, switch</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmChange(null)} className="text-[#C6D4E8] text-xs h-7">Cancel</Button>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Button onClick={() => setAsCareerGoal(false)} disabled={settingCareer || careerSet}
              className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold" data-testid="set-career-goal-btn">
              {careerSet ? <><Check className="w-4 h-4 mr-2" /> Goal Set</> : settingCareer ? 'Saving...' : <><Target className="w-4 h-4 mr-2" /> Set as My Career Goal</>}
            </Button>
            <Button onClick={addToGoals} disabled={addingGoal || addedGoal} variant="outline" className="border-[rgba(57,227,220,0.12)] text-slate-300" data-testid="add-career-goal-btn">
              {addedGoal ? <><Check className="w-4 h-4 mr-2" /> Saved</> : addingGoal ? 'Saving...' : 'Add to Roadmap'}
            </Button>
            <Button variant="outline" onClick={() => window.open(`https://www.onetonline.org/find/quick?s=${encodeURIComponent(career.title)}`, '_blank')} className="border-[rgba(57,227,220,0.12)] text-slate-300">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Compact career card (list item style) ─── */
function CareerCard({ career, index, onSelect }) {
  const hasBls = career.bls_data && career.bls_data.median;
  const salary = hasBls
    ? `$${Math.round(career.bls_data.median / 1000)}K`
    : career.salary_range?.split('-')[0]?.trim() || '-';

  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.5) }}
      onClick={() => onSelect(career)}
      className="w-full text-left p-4 rounded-xl border border-[rgba(57,227,220,0.12)]/40 bg-slate-800/20 hover:bg-slate-800/40 hover:border-cyan-500/30 transition-all group"
      data-testid={`career-card-${index}`}>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-blue-600/15 flex items-center justify-center shrink-0">
          <Briefcase className="w-5 h-5 text-[#39E3DC]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-semibold text-sm group-hover:text-cyan-200 transition-colors truncate">{career.title}</h3>
            {career.match_score && (
              <span className="flex items-center gap-1 text-amber-400 text-xs font-semibold shrink-0">
                <Star className="w-3 h-3 fill-amber-400" /> {career.match_score}%
              </span>
            )}
            {hasBls && <span className="text-[9px] text-slate-300 bg-slate-800 px-1.5 py-0.5 rounded shrink-0">BLS</span>}
          </div>
          <p className="text-[#C6D4E8] text-xs mt-0.5 line-clamp-1">{career.description}</p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-emerald-400 font-mono font-bold text-sm">{salary}</p>
            <p className="text-slate-300 text-[10px]">{career.growth_outlook}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-cyan-300 transition-colors" />
        </div>
      </div>
      <CareerTags tags={career.tags} className="mt-2" />
    </motion.button>
  );
}

/* ─── Sort + utility functions ─── */
const EDU_RANK = ['high school', 'certificate', 'apprenticeship', 'associate', 'bachelor', 'master', 'doctoral'];
const salaryOf = (c) => {
  const direct = c.bls_data?.median;
  if (direct) return Number(direct);
  const nums = String(c.salary_range || '').match(/\d[\d,]{3,}/g);
  if (!nums?.length) return 0;
  const vals = nums.map(n => Number(n.replace(/,/g, '')));
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
};
const tagText = (c) => (c.tags || []).map(t => String(t.label || t).toLowerCase()).join(' ');
const hasTag = (c, needle) => tagText(c).includes(needle);
const eduScore = (c) => { const e = String(c.education_required || '').toLowerCase(); const i = EDU_RANK.findIndex(r => e.includes(r)); return i === -1 ? 99 : i; };

const sortCareers = (careers, mode) => {
  const list = [...(careers || [])];
  const byTitle = (a, b) => String(a.title || '').localeCompare(String(b.title || ''));
  const fns = {
    az:        byTitle,
    salary:    (a, b) => salaryOf(b) - salaryOf(a) || byTitle(a, b),
    growth:    (a, b) => (hasTag(b, 'grow') ? 1 : 0) - (hasTag(a, 'grow') ? 1 : 0) || byTitle(a, b),
    education: (a, b) => eduScore(a) - eduScore(b) || byTitle(a, b),
    ai:        (a, b) => (hasTag(a, 'ai') || hasTag(a, 'automat') ? 1 : 0) - (hasTag(b, 'ai') || hasTag(b, 'automat') ? 1 : 0) || byTitle(a, b),
    match:     (a, b) => (b.match_score || 0) - (a.match_score || 0) || byTitle(a, b),
  };
  return list.sort(fns[mode] || fns.match);
};

/* ─── Explore: interest categories ─── */
const INTEREST_CATEGORIES = [
  { key: 'engineering', label: 'Engineering', icon: Wrench, action: { type: 'field', value: 'Engineering' }, hue: '#39E3DC' },
  { key: 'technology', label: 'Technology', icon: Cpu, action: { type: 'field', value: 'Technology' }, hue: '#60a5fa' },
  { key: 'business', label: 'Business', icon: Building, action: { type: 'field', value: 'Business' }, hue: '#a78bfa' },
  { key: 'entrepreneurship', label: 'Entrepreneurship', icon: Rocket, action: { type: 'route', to: '/app/paths/entrepreneurship' }, hue: '#f472b6' },
  { key: 'healthcare', label: 'Healthcare', icon: Heart, action: { type: 'field', value: 'Healthcare' }, hue: '#34d399' },
  { key: 'aviation', label: 'Aviation', icon: Plane, action: { type: 'search', value: 'pilot' }, hue: '#22d3ee' },
  { key: 'sports', label: 'Sports', icon: Trophy, action: { type: 'search', value: 'athletic trainer' }, hue: '#fbbf24' },
  { key: 'creative', label: 'Creative Careers', icon: Palette, action: { type: 'field', value: 'Arts, Media & Design' }, hue: '#f87171' },
  { key: 'trades', label: 'Skilled Trades', icon: Wrench, action: { type: 'route', to: '/app/paths/trades' }, hue: '#fb923c' },
  { key: 'science', label: 'Science', icon: Microscope, action: { type: 'field', value: 'Science' }, hue: '#a3e635' },
  { key: 'public', label: 'Public Service', icon: Landmark, action: { type: 'field', value: 'Public Safety' }, hue: '#38bdf8' },
  { key: 'education', label: 'Education', icon: GraduationCap, action: { type: 'field', value: 'Education' }, hue: '#c084fc' },
  { key: 'finance', label: 'Finance', icon: DollarSign, action: { type: 'field', value: 'Finance' }, hue: '#34d399' },
  { key: 'law', label: 'Law', icon: Scale, action: { type: 'field', value: 'Law & Government' }, hue: '#93c5fd' },
  { key: 'military', label: 'Military', icon: Shield, action: { type: 'route', to: '/app/paths/military' }, hue: '#22d3ee' },
  { key: 'environment', label: 'Environment', icon: Leaf, action: { type: 'field', value: 'Agriculture & Forestry' }, hue: '#34d399' },
];

const fmtPay = (n) => (n ? `$${Math.round(n / 1000)}K` : null);

/* ─── AI recommendation card ─── */
function RecommendationCard({ card, index, onExplore, onSave, onCompare, onMore, onDismiss, inCompare }) {
  const pay = fmtPay(card.median_wage_annual);
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.05, 0.4) }}
      className="fp-rail glass-card relative overflow-hidden p-4 flex flex-col" data-testid={`recommendation-card-${index}`}>
      <div aria-hidden className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full blur-3xl opacity-20"
        style={{ background: 'radial-gradient(circle,#39E3DC,transparent 65%)' }} />
      <div className="flex items-start justify-between gap-3 relative">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.14em] text-[#39E3DC] font-bold truncate">{card.field}</p>
          <h3 className="text-white font-bold text-base leading-tight mt-0.5">{card.title}</h3>
        </div>
        <div className="shrink-0 text-center rounded-xl px-2.5 py-1.5 border border-cyan-400/30 bg-cyan-500/10">
          <div className="font-outfit text-lg font-extrabold text-white tabular-nums leading-none">{card.match_score}%</div>
          <div className="text-[8px] uppercase tracking-wider text-[#39E3DC] font-bold mt-0.5">match</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {pay && <span className="fp-pill"><DollarSign className="w-3 h-3 text-emerald-400" /> {pay}/yr</span>}
        {card.projected_growth_pct_10yr != null && <span className="fp-pill"><TrendingUp className="w-3 h-3 text-cyan-300" /> +{card.projected_growth_pct_10yr}%</span>}
        {card.typical_education && <span className="fp-pill"><GraduationCap className="w-3 h-3 text-blue-300" /> {card.typical_education}</span>}
      </div>
      <div className="mt-3 rounded-xl border border-white/8 bg-white/[0.03] p-3 flex-1">
        <p className="text-[10px] uppercase tracking-[0.14em] text-slate-300 font-bold mb-1.5 flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-[#a78bfa]" /> Why this fits you</p>
        <ul className="space-y-1">
          {(card.reasons || []).map((r, i) => (
            <li key={i} className="text-[12px] text-slate-300 flex items-start gap-2"><Check className="w-3 h-3 text-[#39E3DC] mt-0.5 shrink-0" /><span>{r}</span></li>
          ))}
        </ul>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <button onClick={() => onExplore(card)} data-testid={`rec-explore-${card.soc_code}`} className="btn-primary text-xs px-4 py-1.5 flex items-center gap-1">Explore <ArrowRight className="w-3 h-3" /></button>
        <button onClick={() => onSave(card)} data-testid={`rec-save-${card.soc_code}`} title="Save" className={`p-1.5 rounded-lg border transition-colors ${card.saved ? 'border-cyan-400/60 bg-cyan-500/15 text-cyan-200' : 'border-white/12 text-slate-300 hover:text-white hover:border-white/30'}`}>{card.saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}</button>
        <button onClick={() => onCompare(card)} data-testid={`rec-compare-${card.soc_code}`} title="Compare" className={`p-1.5 rounded-lg border transition-colors ${inCompare ? 'border-purple-400/60 bg-purple-500/15 text-purple-200' : 'border-white/12 text-slate-300 hover:text-white hover:border-white/30'}`}><GitCompare className="w-4 h-4" /></button>
        <button onClick={() => onMore(card)} data-testid={`rec-more-${card.soc_code}`} title="More like this" className="p-1.5 rounded-lg border border-white/12 text-slate-300 hover:text-white hover:border-white/30 transition-colors"><ThumbsUp className="w-4 h-4" /></button>
        <button onClick={() => onDismiss(card)} data-testid={`rec-dismiss-${card.soc_code}`} title="Not interested" className="p-1.5 rounded-lg border border-white/12 text-slate-300 hover:text-red-300 hover:border-red-400/40 transition-colors ml-auto"><ThumbsDown className="w-4 h-4" /></button>
      </div>
    </motion.div>
  );
}

/* ─── Compare modal ─── */
function CompareModal({ items, onClose, onRemove }) {
  const rows = [
    ['Field', (c) => c.field || '-'],
    ['Typical pay', (c) => (c.median_wage_annual ? `$${c.median_wage_annual.toLocaleString()}` : '-')],
    ['10-yr growth', (c) => (c.projected_growth_pct_10yr != null ? `+${c.projected_growth_pct_10yr}%` : '-')],
    ['Education', (c) => c.typical_education || '-'],
    ['Match', (c) => `${c.match_score}%`],
  ];
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-[#030C1D]/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="compare-modal">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
        className="glass-card w-full max-w-4xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-white font-bold text-lg flex items-center gap-2"><GitCompare className="w-5 h-5 text-[#a78bfa]" /> Compare careers</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-slate-300" data-testid="compare-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-x-auto">
          <div className="flex gap-3 min-w-max">
            {items.map((c) => (
              <div key={c.soc_code} className="w-52 shrink-0 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`compare-col-${c.soc_code}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="text-white font-bold text-sm leading-tight">{c.title}</span>
                  <button onClick={() => onRemove(c)} className="text-slate-300 hover:text-red-300 shrink-0"><X className="w-3.5 h-3.5" /></button>
                </div>
                {rows.map(([label, fn]) => (
                  <div key={label} className="py-1.5 border-t border-white/8">
                    <p className="text-[10px] uppercase tracking-wider text-slate-300 font-semibold">{label}</p>
                    <p className="text-sm text-slate-200">{fn(c)}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════ MAIN PAGE ═══════════════ */
export default function CareersPage() {
  const { getAuthHeader, refreshUser } = useAuth();
  const { focusModeEnabled, focusedPathType, focusedPathName, getSuggestedCareers, getCurrentPathConfig } = useFocusMode();
  const [loading, setLoading] = useState(false);
  const [searchField, setSearchField] = useState('');
  const [careerData, setCareerData] = useState(null);
  const [entryMode, setEntryMode] = useState('browse');
  const [fields, setFields] = useState([]);
  const [totalOccupations, setTotalOccupations] = useState(0);
  const [sortBy, setSortBy] = useState('match');
  const [selectedField, setSelectedField] = useState('');
  const [fieldsCollapsed, setFieldsCollapsed] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [searchParams] = useSearchParams();
  const deepLinked = useRef(false);
  const resultsRef = useRef(null);
  const navigate = useNavigate();

  // ── Explore Phase 1A state ──
  const [recs, setRecs] = useState(null);
  const [recsMeta, setRecsMeta] = useState({});
  const [recsLoading, setRecsLoading] = useState(true);
  const [savedSocs, setSavedSocs] = useState(new Set());
  const [compare, setCompare] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [wmOptions, setWmOptions] = useState([]);
  const [wmSel, setWmSel] = useState([]);
  const [wmResults, setWmResults] = useState(null);
  const [wmLoading, setWmLoading] = useState(false);
  const [acData, setAcData] = useState(null);
  const [acOpen, setAcOpen] = useState(false);
  const acTimer = useRef(null);
  const [dir, setDir] = useState('');

  useEffect(() => {
    axios.get(`${API}/labor-market/fields`, getAuthHeader())
      .then(r => { setFields((r.data.fields || []).sort((a, b) => a.field.localeCompare(b.field))); setTotalOccupations(r.data.total_occupations || 0); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCareerInsights = useCallback(async (field = '') => {
    setLoading(true);
    const searchTerm = field || searchField;
    try {
      const r = await axios.post(`${API}/labor-market/career-insights`, { career_field: searchTerm }, { ...getAuthHeader(), timeout: 15000 });
      const result = r.data.result;
      setCareerData(result);
      if (result.not_found || !(result.careers || []).length) toast.error(`No verified occupation matched "${searchTerm}"`);
      else setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (e) { showFriendlyError(e, { fallback: 'Failed to get career insights' }); }
    finally { setLoading(false); }
  }, [getAuthHeader, searchField]);

  const handleFieldClick = (fieldName) => {
    setSelectedField(fieldName);
    setSearchField(fieldName);
    setFieldsCollapsed(true);
    fetchCareerInsights(fieldName);
  };

  // Deep link from Discover Me
  useEffect(() => {
    if (deepLinked.current) return;
    const q = (searchParams.get('q') || searchParams.get('career') || searchParams.get('field') || '').trim();
    if (!q) return;
    deepLinked.current = true;
    setSearchField(q);
    setEntryMode(searchParams.get('field') ? 'browse' : 'search');
    fetchCareerInsights(q);
  }, [searchParams, fetchCareerInsights]);

  // ── Load personalised Explore data ──
  const loadRecs = useCallback(() => {
    setRecsLoading(true);
    axios.get(`${API}/explore/recommendations`, getAuthHeader())
      .then((r) => { setRecs(r.data.recommendations || []); setRecsMeta(r.data || {}); })
      .catch(() => setRecs([]))
      .finally(() => setRecsLoading(false));
  }, [getAuthHeader]);

  const loadSaved = useCallback(() => {
    axios.get(`${API}/explore/saved`, getAuthHeader())
      .then((r) => setSavedSocs(new Set((r.data.saved || []).map((c) => c.soc_code))))
      .catch(() => {});
  }, [getAuthHeader]);

  useEffect(() => {
    loadRecs();
    loadSaved();
    axios.get(`${API}/explore/what-matters/options`, getAuthHeader()).then((r) => setWmOptions(r.data.options || [])).catch(() => {});
    axios.get(`${API}/explore/direction`, getAuthHeader()).then((r) => setDir(r.data.current_direction || '')).catch(() => {});
  }, [loadRecs, loadSaved, getAuthHeader]);

  const doFeedback = async (card, action) => {
    try { await axios.post(`${API}/explore/feedback`, { soc_code: card.soc_code, title: card.title, action }, getAuthHeader()); } catch { /* non-blocking */ }
  };

  const openCareer = (card) => {
    setSelectedCareer({
      title: card.title, soc_code: card.soc_code, description: card.description,
      education_required: card.typical_education,
      bls_data: card.median_wage_annual ? { median: card.median_wage_annual } : null,
      match_score: card.match_score,
    });
  };

  const onSave = async (card) => {
    const isSaved = savedSocs.has(card.soc_code);
    await doFeedback(card, isSaved ? 'unsave' : 'save');
    setSavedSocs((prev) => { const n = new Set(prev); if (isSaved) n.delete(card.soc_code); else n.add(card.soc_code); return n; });
    toast.success(isSaved ? 'Removed from saved' : `Saved "${card.title}"`);
  };

  const onCompareToggle = (card) => {
    setCompare((prev) => prev.find((c) => c.soc_code === card.soc_code)
      ? prev.filter((c) => c.soc_code !== card.soc_code)
      : (prev.length >= 4 ? prev : [...prev, card]));
  };

  const onMore = async (card) => { await doFeedback(card, 'more_like_this'); toast.success('Refining your recommendations…'); loadRecs(); };
  const onDismiss = async (card) => {
    await doFeedback(card, 'not_interested');
    setRecs((prev) => (prev || []).filter((c) => c.soc_code !== card.soc_code));
    setWmResults((prev) => (prev ? prev.filter((c) => c.soc_code !== card.soc_code) : prev));
  };

  const runInterest = (action) => {
    if (action.type === 'route') { navigate(action.to); return; }
    setSearchField(action.value);
    fetchCareerInsights(action.value);
  };

  const toggleWm = async (key) => {
    const sel = wmSel.includes(key) ? wmSel.filter((k) => k !== key) : [...wmSel, key];
    setWmSel(sel);
    if (!sel.length) { setWmResults(null); return; }
    setWmLoading(true);
    try { const r = await axios.post(`${API}/explore/what-matters`, { values: sel }, getAuthHeader()); setWmResults(r.data.recommendations || []); }
    catch { /* ignore */ } finally { setWmLoading(false); }
  };

  const onSearchChange = (v) => {
    setSearchField(v);
    if (acTimer.current) clearTimeout(acTimer.current);
    if (!v || v.trim().length < 2) { setAcData(null); setAcOpen(false); return; }
    acTimer.current = setTimeout(() => {
      axios.get(`${API}/explore/autocomplete?q=${encodeURIComponent(v.trim())}`, getAuthHeader())
        .then((r) => { setAcData(r.data); setAcOpen(true); }).catch(() => {});
    }, 200);
  };
  const pickCareer = (title) => { setAcOpen(false); setSearchField(title); fetchCareerInsights(title); };
  const pickRoute = (to) => { setAcOpen(false); navigate(to); };

  const sortedCareers = careerData?.careers ? sortCareers(careerData.careers, sortBy) : [];

  return (
    <AppLayout>
      {/* ── Explore hero: universal search ── */}
      <div className="relative overflow-hidden rounded-[18px] px-5 py-7 sm:px-8 sm:py-9 mb-6" data-testid="explore-hero"
        style={{ background: 'linear-gradient(150deg, rgba(120,47,225,0.18), rgba(6,23,54,0.75) 60%)', border: '1px solid rgba(57,227,220,0.25)', boxShadow: '0 0 40px -20px rgba(57,227,220,0.5)' }}>
        <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full blur-3xl opacity-30" style={{ background: 'radial-gradient(circle,#39E3DC,transparent 65%)' }} />
        <div aria-hidden className="pointer-events-none absolute -left-16 bottom-0 h-48 w-48 rounded-full blur-3xl opacity-25" style={{ background: 'radial-gradient(circle,#782FE1,transparent 65%)' }} />
        <div className="relative max-w-3xl">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="fp-pill"><Compass className="w-3 h-3 text-[#39E3DC]" /> Explore</span>
            {dir && (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-300" data-testid="explore-direction-readout">
                <Sparkle className="w-3 h-3 text-[#a78bfa]" /> Current direction: <span className="text-white font-semibold">{dir}</span>
              </span>
            )}
          </div>
          <h1 className="font-outfit text-3xl sm:text-4xl font-extrabold text-white tracking-tight mt-3" style={{ textShadow: '0 0 40px rgba(57,227,220,0.2)' }}>What do you want to explore?</h1>
          <p className="text-sm text-slate-300/80 mt-2">Search anything you're curious about, a career, a college, a trade, the military, aviation, sports, or a business idea.</p>

          <div className="relative mt-5" data-testid="universal-search">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#39E3DC] z-10" />
              <input
                value={searchField}
                onChange={(e) => onSearchChange(e.target.value)}
                onFocus={() => { if (acData) setAcOpen(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { setAcOpen(false); fetchCareerInsights(); } if (e.key === 'Escape') setAcOpen(false); }}
                placeholder="Search careers, colleges, trades, military careers, sports, aviation, businesses, or interests..."
                data-testid="universal-search-input"
                className="w-full rounded-full bg-[#030C1D]/70 border border-cyan-400/30 focus:border-cyan-300 focus:ring-2 focus:ring-cyan-400/30 outline-none text-white placeholder:text-slate-300 pl-12 pr-28 py-3.5 text-sm transition-all" />
              <button onClick={() => { setAcOpen(false); fetchCareerInsights(); }} disabled={loading} data-testid="universal-search-btn"
                className="btn-primary absolute right-1.5 top-1/2 -translate-y-1/2 text-xs px-5 py-2">
                {loading ? <LoadingSpinner size="sm" /> : 'Search'}
              </button>
            </div>
            <AnimatePresence>
              {acOpen && acData && (acData.careers?.length || acData.routes?.length || acData.topics?.length) ? (
                <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  className="absolute z-30 left-0 right-0 mt-2 rounded-2xl border border-cyan-400/25 overflow-hidden backdrop-blur-xl max-h-[60vh] overflow-y-auto"
                  style={{ background: 'rgba(6,23,54,0.97)', boxShadow: '0 20px 60px -20px rgba(0,0,0,0.7), 0 0 30px -10px rgba(57,227,220,0.3)' }}
                  data-testid="autocomplete-dropdown">
                  {acData.careers?.length > 0 && (
                    <div className="py-2">
                      <p className="px-4 py-1 text-[10px] uppercase tracking-wider text-slate-300 font-bold">Careers</p>
                      {acData.careers.map((c) => (
                        <button key={c.soc_code} onMouseDown={() => pickCareer(c.label)} data-testid={`ac-career-${c.soc_code}`}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-cyan-500/10 text-left transition-colors">
                          <Briefcase className="w-4 h-4 text-[#39E3DC] shrink-0" />
                          <span className="text-sm text-white flex-1 truncate">{c.label}</span>
                          <span className="text-[10px] text-slate-300">{c.sublabel}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {acData.topics?.length > 0 && (
                    <div className="py-2 border-t border-white/8">
                      <p className="px-4 py-1 text-[10px] uppercase tracking-wider text-slate-300 font-bold">Possibilities</p>
                      {acData.topics.map((t, i) => (
                        <button key={i} onMouseDown={() => pickCareer(t.search)} data-testid={`ac-topic-${i}`}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-purple-500/10 text-left transition-colors">
                          <Sparkles className="w-4 h-4 text-[#a78bfa] shrink-0" />
                          <span className="text-sm text-slate-200 flex-1 truncate">{t.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {acData.routes?.length > 0 && (
                    <div className="py-2 border-t border-white/8">
                      <p className="px-4 py-1 text-[10px] uppercase tracking-wider text-slate-300 font-bold">Ways to get there</p>
                      {acData.routes.map((r) => (
                        <button key={r.key} onMouseDown={() => pickRoute(r.to)} data-testid={`ac-route-${r.key}`}
                          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-cyan-500/10 text-left transition-colors">
                          <ArrowRight className="w-4 h-4 text-cyan-300 shrink-0" />
                          <span className="text-sm text-white flex-1">{r.label}</span>
                          <span className="text-[10px] text-slate-300">Route</span>
                        </button>
                      ))}
                    </div>
                  )}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* ── When viewing search results, offer a way back to discovery ── */}
      {careerData && !loading && (
        <button onClick={() => { setCareerData(null); setSearchField(''); setSelectedField(''); }} data-testid="back-to-discover"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-cyan-300 hover:text-cyan-200">
          <ChevronRight className="w-4 h-4 rotate-180" /> Back to Explore
        </button>
      )}

      {/* ── Discover (shown when not actively viewing search results) ── */}
      {!careerData && !loading && (
        <div className="space-y-8" data-testid="explore-discover">
          {/* Recommended for You */}
          <section data-testid="recommended-for-you">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div>
                <h2 className="font-outfit text-lg font-bold text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#39E3DC]" /> Recommended for you</h2>
                {(recsMeta.built_from || []).length > 0 && (
                  <p className="text-xs text-slate-300 mt-1">Built from {recsMeta.built_from.map((b) => b.label).join(' · ')}</p>
                )}
              </div>
            </div>
            {recsLoading ? (
              <div className="py-10 flex justify-center"><LoadingSpinner /></div>
            ) : (recs || []).length === 0 ? (
              <GlassCard className="text-center py-8"><p className="text-slate-300 text-sm">Finish your <Link to="/app/assessment" className="text-cyan-300 underline">Assessment</Link> and <Link to="/app/calculator" className="text-cyan-300 underline">Design My Life</Link> and we'll fill this with careers matched to you.</p></GlassCard>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {(recs || []).map((card, i) => (
                  <RecommendationCard key={card.soc_code} card={{ ...card, saved: savedSocs.has(card.soc_code) }} index={i}
                    inCompare={!!compare.find((c) => c.soc_code === card.soc_code)}
                    onExplore={openCareer} onSave={onSave} onCompare={onCompareToggle} onMore={onMore} onDismiss={onDismiss} />
                ))}
              </div>
            )}
          </section>

          {/* Explore by Interest */}
          <section data-testid="explore-by-interest">
            <h2 className="font-outfit text-lg font-bold text-white flex items-center gap-2 mb-3"><Compass className="w-4 h-4 text-[#a78bfa]" /> Explore by interest</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {INTEREST_CATEGORIES.map((cat, i) => {
                const Icon = cat.icon;
                return (
                  <motion.button key={cat.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}
                    onClick={() => runInterest(cat.action)} data-testid={`interest-${cat.key}`}
                    className="fp-tile group relative overflow-hidden p-4 text-left flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110" style={{ background: `${cat.hue}1f`, boxShadow: `0 0 14px -4px ${cat.hue}` }}>
                      <Icon className="w-4 h-4" style={{ color: cat.hue }} />
                    </span>
                    <span className="text-sm font-semibold text-white">{cat.label}</span>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-cyan-300 ml-auto transition-colors" />
                  </motion.button>
                );
              })}
            </div>
          </section>

          {/* What matters to you */}
          <section data-testid="what-matters">
            <h2 className="font-outfit text-lg font-bold text-white flex items-center gap-2 mb-1"><Heart className="w-4 h-4 text-[#f472b6]" /> What matters to you?</h2>
            <p className="text-xs text-slate-300 mb-3">Pick what you care about and we'll surface careers that deliver it.</p>
            <div className="flex flex-wrap gap-2">
              {wmOptions.map((o) => {
                const on = wmSel.includes(o.key);
                return (
                  <button key={o.key} onClick={() => toggleWm(o.key)} data-testid={`wm-${o.key}`}
                    className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${on ? 'border-pink-400/60 bg-pink-500/15 text-pink-100 shadow-[0_0_18px_-6px_rgba(244,114,182,0.6)]' : 'border-white/12 bg-white/[0.04] text-slate-300 hover:text-white hover:border-white/30'}`}>
                    {o.label}
                  </button>
                );
              })}
            </div>
            {wmLoading && <div className="py-6 flex justify-center"><LoadingSpinner /></div>}
            {wmResults && wmResults.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-4" data-testid="wm-results">
                {wmResults.map((card, i) => (
                  <RecommendationCard key={card.soc_code} card={{ ...card, saved: savedSocs.has(card.soc_code) }} index={i}
                    inCompare={!!compare.find((c) => c.soc_code === card.soc_code)}
                    onExplore={openCareer} onSave={onSave} onCompare={onCompareToggle} onMore={onMore} onDismiss={onDismiss} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && <div className="py-12 flex justify-center"><LoadingSpinner /></div>}

      {/* ── Results ── */}
      {careerData && !loading && (
        <div ref={resultsRef} className="space-y-4">

          {/* Sort bar */}
          {sortedCareers.length > 1 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="career-filters">
              <span className="text-xs uppercase tracking-wider text-[#C6D4E8] font-semibold mr-1">
                Sort {sortedCareers.length} careers by
              </span>
              {[['match', 'Best match'], ['az', 'A-Z'], ['salary', 'Salary'], ['growth', 'Growth'], ['education', 'Least education'], ['ai', 'Lowest AI exposure']].map(([key, label]) => (
                <button key={key} onClick={() => setSortBy(key)} data-testid={`sort-${key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                    sortBy === key ? 'border-cyan-300/60 bg-cyan-300/15 text-white' : 'border-white/12 text-slate-300 hover:text-white hover:border-white/30'}`}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Not found */}
          {careerData.not_found && (
            <GlassCard data-testid="career-not-found">
              <h3 className="text-lg font-semibold text-white mb-2">No verified occupation found</h3>
              <p className="text-[#C6D4E8] text-sm mb-4">{careerData.advice}</p>
              <div className="flex flex-wrap gap-2">
                {(careerData.suggestions || []).map(sug => (
                  <button key={sug} onClick={() => { setSearchField(sug); fetchCareerInsights(sug); }}
                    className="text-xs px-3 py-1.5 rounded-full border border-[rgba(57,227,220,0.12)] text-slate-300 hover:border-blue-500/50 hover:text-blue-300 transition-colors">
                    {sug}
                  </button>
                ))}
              </div>
            </GlassCard>
          )}

          {/* Career list */}
          {sortedCareers.length > 0 && (
            <div className="space-y-2" data-testid="career-results-grid">
              {sortedCareers.map((career, i) => (
                <CareerCard key={career.soc_code || i} career={career} index={i} onSelect={setSelectedCareer} />
              ))}
            </div>
          )}

          {/* O*NET attribution */}
          {sortedCareers.length > 0 && (
            <div className="mt-6 p-4 bg-slate-900/50 rounded-xl border border-slate-800 flex flex-col sm:flex-row items-center gap-4" data-testid="onet-attribution">
              <a href="https://services.onetcenter.org/" target="_blank" rel="noopener noreferrer">
                <img src="https://www.onetcenter.org/image/link/onet-in-it.svg" alt="O*NET in-it" className="h-10 opacity-70 hover:opacity-100 transition-opacity" />
              </a>
              <p className="text-[11px] text-slate-300 leading-relaxed">
                This site incorporates information from <a href="https://services.onetcenter.org/" target="_blank" rel="noopener noreferrer" className="text-[#39E3DC] hover:underline">O*NET Web Services</a> by the U.S. Department of Labor. Salary data from the U.S. Bureau of Labor Statistics.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Compare tray ── */}
      {compare.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-full px-4 py-2.5 border border-purple-400/40 backdrop-blur-xl"
          style={{ background: 'rgba(6,23,54,0.95)', boxShadow: '0 12px 40px -12px rgba(120,47,225,0.6)' }} data-testid="compare-tray">
          <span className="text-sm text-white font-medium flex items-center gap-2"><GitCompare className="w-4 h-4 text-[#a78bfa]" /> {compare.length} selected</span>
          <button onClick={() => setCompareOpen(true)} disabled={compare.length < 2} data-testid="compare-open"
            className="btn-primary text-xs px-4 py-1.5 disabled:opacity-50">Compare</button>
          <button onClick={() => setCompare([])} className="text-slate-300 hover:text-white text-xs" data-testid="compare-clear">Clear</button>
        </motion.div>
      )}
      <AnimatePresence>
        {compareOpen && <CompareModal items={compare} onClose={() => setCompareOpen(false)}
          onRemove={(c) => { const next = compare.filter((x) => x.soc_code !== c.soc_code); setCompare(next); if (next.length < 2) setCompareOpen(false); }} />}
      </AnimatePresence>

      {/* Career detail overlay */}
      <AnimatePresence>
        {selectedCareer && <CareerDetailView career={selectedCareer} onClose={() => setSelectedCareer(null)} />}
      </AnimatePresence>
    </AppLayout>
  );
}
